<template>
  <div class="person-detail">
    <!-- Loading state -->
    <div v-if="isLoading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="sr-only">Caricamento...</span>
      </div>
    </div>

    <!-- Error display -->
    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- Success message -->
    <div v-if="successMessage" class="alert alert-success" role="alert">
      {{ successMessage }}
    </div>

    <!-- Header persona -->
    <div v-if="person && !isLoading">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link to="/people">Persone</router-link>
              </li>
              <li class="breadcrumb-item active">{{ person.name }} {{ person.surname }}</li>
            </ol>
          </nav>
          <h2>Dettaglio Persona e Budget</h2>
        </div>
        <div>
          <button 
            class="btn btn-outline-primary me-2" 
            @click="openEditPersonDialog"
            :disabled="isLoading"
          >
            <i class="fas fa-edit me-1"></i> Modifica Persona
          </button>          
        </div>
      </div>
    </div>


    <!-- Layout a due colonne: Budget Annuali (1/3) + Allocazioni Budget (2/3) -->
    <div class="row" v-if="!isLoading">
      <!-- COLONNA SINISTRA: Budget Annuali (1/3) -->
      <div class="col-md-4">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">
              <i class="fas fa-calendar-alt me-2"></i>Budget Annuali
            </h5>
            <button 
              class="btn btn-sm btn-primary"
              @click="openNewBudgetDialog"
              title="Aggiungi budget per un nuovo anno"
            >
              <i class="fas fa-plus me-1"></i>Aggiungi
            </button>
          </div>
          <div class="card-body">
            <div v-if="budgetYears.length === 0" class="text-muted text-center py-4">
              <div class="mb-3">
                <i class="fas fa-calendar-alt fa-3x text-muted"></i>
              </div>
              <p class="small">Nessun budget annuale trovato.</p>
              <p class="small">Clicca su "Aggiungi" per iniziare.</p>
            </div>
            
            <div v-else>
              <!-- Tabella Budget Annuali compatta -->
              <div class="table-responsive">
                <table class="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th class="text-center">
                        <i class="fas fa-calendar me-1"></i>Anno
                      </th>
                      <th class="text-end">
                        <i class="fas fa-euro-sign me-1"></i>Budget
                      </th>
                      <th class="text-center">
                        <i class="fas fa-tools me-1"></i>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="budget in sortedBudgetYears" :key="budget.year">
                      <!-- Anno -->
                      <td class="text-center">
                        <span class="badge bg-primary">
                          {{ budget.year }}
                        </span>
                      </td>
                      
                      <!-- Budget deve fare un confronto con budget_sum (verde uguale rosso meno viola di piu )-->
                      <td class="text-end">
                        <strong class="text-success">
                          € {{ formatCurrency(budget.budget) }}
                        </strong><br/>
                        <div class="small">
                          <span v-if="budget.budget_sum > budget.budget" class="text-extreme-danger">
                            Rendicontato:€ {{ formatCurrency(budget.budget_sum) }}
                          </span>
                          <span v-if="budget.budget_sum < budget.budget" class="text-danger">
                            Rendicontato: € {{ formatCurrency(budget.budget_sum) }}
                          </span>
                          <span v-if="budget.budget_sum === budget.budget" class="text-success">
                            Rendicontato: € {{ formatCurrency(budget.budget_sum) }}
                          </span>
                        </div>
                      </td>
                      
                      <!-- Azioni -->
                      <td class="text-center">
                        <div class="btn-group btn-group-sm" role="group">
                          <button 
                            class="btn btn-outline-primary"
                            @click="openEditBudgetDialog(budget)"
                            title="Modifica"
                          >
                            <i class="fas fa-edit"></i>
                          </button>
                          <button 
                            class="btn btn-outline-danger"
                            @click="deleteBudget(budget.id_people, budget.year)"
                            title="Elimina"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- COLONNA DESTRA: Allocazioni Budget per Progetto (2/3) -->
      <div class="col-md-8">
        <div v-if="budgetAllocations.length > 0" class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">
              <i class="fas fa-project-diagram me-2"></i>Allocazione Budget per Progetto
            </h5>
            <button 
              class="btn btn-sm btn-primary"
              @click="openNewProjectBudgetDialog"
              title="Aggiungi budget per progetto"
            >
              <i class="fas fa-plus me-1"></i>Aggiungi
            </button>
          </div>
          <div class="card-body">
            <TableSortable 
              v-if="budgetAllocations && budgetAllocations.length > 0"
              :data="budgetAllocations" 
              :options="budgetAllocationsTableOptions"
            >
              <template #field-year="{ row }">
                <span class="badge bg-secondary">
                  {{ row.year }}
                </span>
              </template>
              
              <template #field-project_name="{ row }">
                <router-link :to="`/projects/${row.id_project}`" class="text-decoration-none">
                  {{ row.project_name }}
                </router-link>
              </template>
              
              <template #field-tipo_label="{ row }">
                <span class="badge" :class="{
                  'bg-secondary': row.tipo === 0,
                  'bg-warning': row.tipo === 10,
                  'bg-success': row.tipo === 20
                }">
                  {{ row.tipo_label }}
                </span>
              </template>
              
              <template #field-budget="{ row }">
                <span class="fs-6 fw-bold text-success">
                  € {{ formatCurrency(row.budget) }}
                </span>
              </template>
              
              <template #field-actions="{ row }">
                <div class="btn-group btn-group-sm">
                  <button 
                    class="btn btn-outline-primary"
                    @click="openEditProjectBudgetDialog(row)"
                    title="Modifica"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button 
                    class="btn btn-outline-danger"
                    @click="deleteProjectBudget(row.id_budget)"
                    title="Elimina"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </template>
            </TableSortable>
          </div>
        </div>
        
        <!-- Messaggio se non ci sono allocazioni -->
        <div v-else class="card h-100">
          <div class="card-body d-flex align-items-center justify-content-center">
            <div class="text-center text-muted">
              <i class="fas fa-clipboard-list fa-3x mb-3"></i>
              <p>Nessuna allocazione budget trovata per questa persona.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog Modal per Budget Anno -->
    <div 
      v-if="showDialog" 
      class="modal fade show" 
      style="display: block; background-color: rgba(0,0,0,0.5);"
      tabindex="-1" 
      role="dialog"
      @click="handleModalClick"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingBudget ? `Modifica Budget ${editingBudget.year}` : 'Nuovo Budget Anno' }}
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="closeDialog"
              aria-label="Chiudi"
            ></button>
          </div>
          
          <div class="modal-body">
            <!-- Error nel dialog -->
            <div v-if="dialogError" class="alert alert-danger">
              {{ dialogError }}
            </div>
            
            <!-- Form dinamico da schema DBMng -->
            <form @submit.prevent="saveBudget">
              <!-- Anno (solo per nuovo budget) -->
              <div v-if="!editingBudget" class="mb-3">
                <label for="dialog_year" class="form-label">Anno *</label>
                <input 
                  id="dialog_year"
                  v-model.number="formData.year"
                  type="number"
                  min="2020"
                  :max="new Date().getFullYear() + 5"
                  class="form-control"
                  required
                  placeholder="es. 2025"
                >
              </div>

              <!-- Campi dinamici da DBMng (solo budget) -->
              <div 
                v-for="(field, index) in budgetSchema?.fields || []" 
                :key="field.field"
                class="mb-3"
              >
                <label :for="'dialog_' + field.field" class="form-label">
                  {{ field.label }}
                  <span v-if="!field.nullable" class="text-danger">*</span>
                </label>
                
                <!-- Input numerico per budget -->
                <input
                  v-if="field.type === 'float'"
                  :id="'dialog_' + field.field"
                  v-model.number="formData[field.field]"
                  type="number"
                  step="0.01"
                  min="0"
                  :required="!field.nullable"
                  class="form-control"
                  :placeholder="`Inserisci ${field.label.toLowerCase()}`"
                >
                
                <!-- Altri tipi di input -->
                <input
                  v-else
                  :id="'dialog_' + field.field"
                  v-model="formData[field.field]"
                  :type="field.validator === 'email' ? 'email' : 'text'"
                  :required="!field.nullable"
                  class="form-control"
                  :placeholder="`Inserisci ${field.label.toLowerCase()}`"
                >
              </div>
            </form>
          </div>
          
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="closeDialog"
              :disabled="isDialogLoading"
            >
              Annulla
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="saveBudget"
              :disabled="isDialogLoading"
            >
              <span v-if="isDialogLoading" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingBudget ? 'Salva Modifiche' : 'Crea Budget' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog Modal per Modifica Persona -->
    <div 
      v-if="showPersonDialog" 
      class="modal fade show" 
      style="display: block; background-color: rgba(0,0,0,0.5);"
      tabindex="-1" 
      role="dialog"
    >
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Modifica Dati Persona</h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="closePersonDialog"
              aria-label="Chiudi"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="personDialogError" class="alert alert-danger">
              {{ personDialogError }}
            </div>
            
            <DbmngForm
              v-if="personSchema"
              v-model="personFormData"
              :dbmng-schema="personSchema"
              :show-actions="false"
            />
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="closePersonDialog"
              :disabled="isPersonDialogLoading"
            >
              Annulla
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="savePerson" 
              :disabled="isPersonDialogLoading"
            >
              <span v-if="isPersonDialogLoading" class="spinner-border spinner-border-sm me-2"></span>
              Salva Modifiche
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Budget Dialog per Progetti -->
    <BudgetDialog
      v-model="showProjectBudgetDialog"
      :budget="editingProjectBudget"
      :defaults="budgetDefaults"
      :dbmng-schema="dbmngSchemaBudget"
      @saved="onProjectBudgetSaved"
    />

  </div>
</template>

<script>
import DbmngForm from '@/lib/DbmngForm.vue'
import { useAppStore } from '@/stores/app.js'
import BudgetDialog from '@/components/BudgetDialog.vue'
import TableSortable from '@/components/TableSortable.vue'
import budgetAPI from '@/services/budgetAPI.js'

export default {
  name: 'PersonDetail',
  components: {
    DbmngForm,
    BudgetDialog,
    TableSortable
  },
  setup() {
    const store = useAppStore()
    return { store }
  },
  data() {
    return {
      person: null,
      personSchema: null, // Schema per la persona
      budgetYears: [],
      budgetAllocations: [], // Budget allocati per progetto
      budgetSchema: null, // Schema per il budget
      formData: {},
      editingBudget: null,
      isLoading: false,
      error: null,
      successMessage: null,
      // Budget Dialog state
      showDialog: false,
      isDialogLoading: false,
      dialogError: null,
      // Person Dialog state
      showPersonDialog: false,
      isPersonDialogLoading: false,
      personDialogError: null,
      personFormData: {},
      // Project Budget Dialog state (for allocations table)
      showProjectBudgetDialog: false,
      editingProjectBudget: null,
      budgetDefaults: {},
      dbmngSchemaBudget: null
    }
  },
  
  computed: {
    personId() {
      return parseInt(this.$route.params.id)
    },
    
    sortedBudgetYears() {
      return [...this.budgetYears].sort((a, b) => a.year - b.year)
    },
    
    budgetAllocationsTableOptions() {
      return {
        structure: [
          { key: 'year', label: 'Anno', sortable: true, align: 'center' },
          { key: 'project_name', label: 'Progetto', sortable: true, filterable: true },
          { key: 'tipo_label', label: 'Tipo', sortable: true, align: 'center' },
          { key: 'budget', label: 'Budget', sortable: true, align: 'right' },
          { key: 'actions', label: 'Azioni', sortable: false, align: 'center' }
        ]
      }
    }
  },
  
  async mounted() {
    await this.loadPersonAndBudgets()
  },
  
  methods: {
    async loadPersonAndBudgets() {
      try {
        this.isLoading = true
        this.error = null
        
        // Carica dati persona + schema
        const personResult = await this.store.apiCall(`/people/people/${this.personId}`, { method: 'GET' })
        if (!personResult.ok || !personResult.data) {
          throw new Error('Persona non trovata')
        }
        this.person = personResult.data[0]
        this.personSchema = personResult.dbmng
        this.budgetAllocations = personResult.budget_details || []
        
        // Carica budget anni + schema DBMng
        const budgetResult = await this.store.apiCall(`/people/people/${this.personId}/year`, { method: 'GET' })
        
        if (budgetResult.ok) {
          this.budgetYears = budgetResult.data || []
          this.budgetSchema = budgetResult.dbmng
          this.initializeFormData()
        } else {
          this.error = budgetResult.message || 'Errore nel caricamento dei budget'
        }
        
        // Carica schema per budget progetti se non presente
        if (!this.dbmngSchemaBudget) {
          const budgetSchemaRes = await budgetAPI.getAll()
          if (budgetSchemaRes.dbmng) {
            this.dbmngSchemaBudget = budgetSchemaRes.dbmng
          }
        }
      } catch (error) {
        this.error = error.message || 'Errore di connessione'
        console.error('Errore loadPersonAndBudgets:', error)
      } finally {
        this.isLoading = false
      }
    },
    
    initializeFormData() {
      this.formData = { year: new Date().getFullYear() }
      if (this.budgetSchema?.fields && Array.isArray(this.budgetSchema.fields)) {
        this.budgetSchema.fields.forEach(field => {
          this.formData[field.field] = ''
        })
      }
    },
    
    // --- Gestione Budget ---
    
    async saveBudget() {
      try {
        this.isDialogLoading = true
        this.dialogError = null
        this.error = null
        this.successMessage = null
        
        const budgetData = {}
        // Copia solo i campi DBMng (budget), non year
        if (this.budgetSchema?.fields && Array.isArray(this.budgetSchema.fields)) {
          this.budgetSchema.fields.forEach(field => {
            budgetData[field.field] = this.formData[field.field]
          })
        }
        
        let result
        
        if (this.editingBudget) {
          // Update esistente
          result = await this.store.apiCall(
            `/people/people/${this.personId}/year/${this.editingBudget.year}`, 
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(budgetData)
            }
          )
          this.successMessage = `Budget ${this.editingBudget.year} aggiornato con successo`
        } else {
          // Nuovo budget
          result = await this.store.apiCall(
            `/people/people/${this.personId}/year/${this.formData.year}`, 
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(budgetData)
            }
          )
          this.successMessage = `Budget ${this.formData.year} creato con successo`
        }
        
        if (result.ok) {
          this.closeDialog()
          this.loadPersonAndBudgets() // Ricarica la lista
        } else {
          this.dialogError = result.message || 'Errore nel salvataggio'
        }
      } catch (error) {
        this.dialogError = error.message || 'Errore di connessione'
        console.error('Errore saveBudget:', error)
      } finally {
        this.isDialogLoading = false
      }
    },
    
    openNewBudgetDialog() {
      this.editingBudget = null
      this.initializeFormData()
      this.dialogError = null
      this.showDialog = true
    },
    
    openEditBudgetDialog(budget) {
      this.editingBudget = budget
      
      // Popola il form con i dati del budget
      this.formData = { year: budget.year }
      if (this.budgetSchema?.fields && Array.isArray(this.budgetSchema.fields)) {
        this.budgetSchema.fields.forEach(field => {
          this.formData[field.field] = budget[field.field] || ''
        })
      }
      
      this.dialogError = null
      this.showDialog = true
    },
    
    closeDialog() {
      this.showDialog = false
      this.editingBudget = null
      this.dialogError = null
      this.isDialogLoading = false
      this.initializeFormData()
    },
    
    handleModalClick(event) {
      // Modale non si chiude quando si clicca fuori
      // if (event.target === event.currentTarget) {
      //   this.closeDialog()
      // }
    },
    
    async deleteBudget(peopleId, year) {
      if (!confirm(`Sei sicuro di voler eliminare il budget per l'anno ${year}?`)) {
        return
      }
      
      try {
        this.isLoading = true
        this.error = null
        this.successMessage = null
        
        const result = await this.store.apiCall(
          `/people/people/${peopleId}/year/${year}`, 
          { method: 'DELETE' }
        )
        
        if (result.ok) {
          this.successMessage = `Budget ${year} eliminato con successo`
          this.loadPersonAndBudgets() // Ricarica la lista
        } else {
          this.error = result.message || 'Errore nell\'eliminazione'
        }
      } catch (error) {
        this.error = error.message || 'Errore di connessione'
        console.error('Errore deleteBudget:', error)
      } finally {
        this.isLoading = false
      }
    },
    
    // --- Gestione Budget Progetti ---
    
    openNewProjectBudgetDialog() {
      this.editingProjectBudget = null
      this.budgetDefaults = {
        id_people: this.person.id_people,
        year: new Date().getFullYear()
      }
      this.showProjectBudgetDialog = true
    },
    
    openEditProjectBudgetDialog(budget) {
      this.editingProjectBudget = budget
      this.budgetDefaults = {}
      this.showProjectBudgetDialog = true
    },
    
    async deleteProjectBudget(budgetId) {
      if (!confirm('Sei sicuro di voler eliminare questo budget?')) {
        return
      }
      
      try {
        const result = await budgetAPI.delete(budgetId)
        if (result.ok) {
          this.successMessage = 'Budget eliminato con successo'
          this.loadPersonAndBudgets()
          setTimeout(() => { this.successMessage = null }, 3000)
        } else {
          this.error = result.message
        }
      } catch (e) {
        this.error = e.message
      }
    },
    
    onProjectBudgetSaved() {
      this.successMessage = 'Budget salvato con successo'
      this.loadPersonAndBudgets()
      setTimeout(() => { this.successMessage = null }, 3000)
    },
    
    // --- Gestione Modifica Persona ---
    
    openEditPersonDialog() {
      // Clona i dati della persona per evitare modifiche dirette alla vista prima del salvataggio
      this.personFormData = { ...this.person }
      this.personDialogError = null
      this.showPersonDialog = true
    },
    
    closePersonDialog() {
      this.showPersonDialog = false
      this.personDialogError = null
      this.isPersonDialogLoading = false
    },
    
    async savePerson() {
      try {
        this.isPersonDialogLoading = true
        this.personDialogError = null
        this.error = null
        this.successMessage = null
        
        const result = await this.store.apiCall(
          `/people/people/${this.personId}`, 
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(this.personFormData)
          }
        )
        
        if (result.ok) {
          this.successMessage = 'Dati persona aggiornati con successo'
          this.closePersonDialog()
          this.loadPersonAndBudgets() // Ricarica i dati
        } else {
          this.personDialogError = result.message || 'Errore nel salvataggio'
        }
      } catch (error) {
        this.personDialogError = error.message || 'Errore di connessione'
        console.error('Errore savePerson:', error)
      } finally {
        this.isPersonDialogLoading = false
      }
    },
    
    formatCurrency(value) {
      if (!value) return '0,00'
      return parseFloat(value).toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        return new Date(dateString).toLocaleString('it-IT')
      } catch (error) {
        return 'Data non valida'
      }
    }
  }
}
</script>

<style scoped>
.person-detail {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.breadcrumb {
  background-color: transparent;
  padding: 0;
  margin-bottom: 1rem;
}

.card {
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

/* Dialog Modal Styles */
.modal.show {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-dialog {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-content {
  border: none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.modal-title {
  font-weight: 600;
  color: #495057;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: bold;
  color: #000;
  opacity: 0.5;
  cursor: pointer;
}

.btn-close:hover {
  opacity: 0.8;
}

.btn-close:before {
  content: '×';
}

@media (max-width: 768px) {
  .person-detail {
    padding: 10px;
  }
  
  .col-md-6 {
    margin-bottom: 1rem;
  }
}
</style>